"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Tooltip } from "@/components/common/ui/Tooltip";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";

/**
 * Copyable single-line value rendered inside a tooltip.
 * Used to show the full content of truncated quote-ID parts on hover.
 */
function TooltipCopyLine({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="group flex items-center gap-1.5 w-full text-left cursor-pointer hover:bg-gray-50 -mx-1 px-1 py-0.5 rounded"
    >
      <span className="font-mono text-sm text-gray-700 break-all">{value}</span>
      {copied ? (
        <Check className="w-3 h-3 flex-shrink-0 text-green-500" />
      ) : (
        <Copy className="w-3 h-3 flex-shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors" />
      )}
    </button>
  );
}

/**
 * Small copyable ID chip with hover-to-reveal copy icon and click-to-copy.
 * Optionally wraps itself in a tooltip that shows the full value.
 */
function CopyableId({
  value,
  className,
  showTooltip = true,
}: {
  value: string;
  className?: string;
  showTooltip?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const button = (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className={cn("group flex items-center gap-1 cursor-pointer", className)}
    >
      <span className="truncate font-mono min-w-0">{value}</span>
      {copied ? (
        <Check className="w-3 h-3 flex-shrink-0 text-green-500" />
      ) : (
        <Copy className="w-3 h-3 flex-shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );

  if (!showTooltip) return button;

  return (
    <Tooltip
      content={<TooltipCopyLine value={value} />}
      placement="top"
      className="max-w-full! inline-flex"
    >
      {button}
    </Tooltip>
  );
}

function splitParts(quoteId: string): string[] {
  return quoteId
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Normalize a string or array into a list of parts. */
function normalizeParts(
  quoteId: string | undefined,
  quoteIdParts: string[] | undefined,
): string[] {
  if (quoteIdParts && quoteIdParts.length > 0) return quoteIdParts;
  if (quoteId) return splitParts(quoteId);
  return [];
}

/**
 * Compact display: first part truncated + "+N" badge (with tooltip) when multi-part.
 * Used in the collapsed main row.
 */
export function QuoteIdCompact({
  quoteId,
  quoteIdParts,
}: {
  quoteId?: string;
  quoteIdParts?: string[];
}) {
  const parts = normalizeParts(quoteId, quoteIdParts);
  if (parts.length === 0) return null;

  const extra = parts.length - 1;

  return (
    <div className="flex items-center gap-1 max-w-full">
      <div className="min-w-0 shrink max-w-full">
        <CopyableId
          value={parts[0]}
          className="text-sm text-gray-700 hover:text-gray-900 min-w-0"
        />
      </div>
      {extra > 0 && (
        <Tooltip
          content={
            <div className="flex flex-col gap-0.5">
              {parts.slice(1).map((p, i) => (
                <TooltipCopyLine key={i} value={p} />
              ))}
            </div>
          }
          placement="top"
        >
          <Typography
            as="span"
            variant="small"
            weight="medium"
            color="text-gray-500"
            className="flex-shrink-0 px-1 py-px rounded bg-gray-100 cursor-default"
          >
            +{extra}
          </Typography>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * Expanded display: all parts as chips separated by `/`.
 * Used in the expanded detail grid.
 */
export function QuoteIdExpanded({
  quoteId,
  quoteIdParts,
}: {
  quoteId?: string;
  quoteIdParts?: string[];
}) {
  const parts = normalizeParts(quoteId, quoteIdParts);
  if (parts.length === 0) return null;

  if (parts.length === 1) {
    return (
      <CopyableId
        value={parts[0]}
        className="text-sm text-gray-900 hover:text-gray-700"
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {parts.map((part, i) => (
        <span key={i} className="contents">
          {i > 0 && (
            <Typography
              as="span"
              variant="small"
              color="text-gray-300"
              className="mx-0.5 px-2"
            >
              /
            </Typography>
          )}
          <CopyableId
            value={part}
            className="text-sm text-gray-900 hover:text-gray-700 px-1.5 py-0.5 rounded bg-gray-50 border border-gray-100"
          />
        </span>
      ))}
    </div>
  );
}
